import type { RagCounts, RagDistribution } from "@/types";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  OVERALL_BAR_CLASS,
  OVERALL_META,
  RAG_META,
  type OverallStatus,
} from "./report-status-meta";

/**
 * T-44b — RAG distribution across active projects, from each project's latest SUBMITTED weekly
 * report. Every bar sits next to its text label and count, so colour is never the only carrier
 * (WCAG 1.4.1); the bars themselves are aria-hidden.
 */
const OVERALL_ROWS: { key: OverallStatus | "no_report"; label: string }[] = [
  { key: "on_track", label: OVERALL_META.on_track.label },
  { key: "at_risk", label: OVERALL_META.at_risk.label },
  { key: "off_track", label: OVERALL_META.off_track.label },
  { key: "no_report", label: "No report submitted" },
];

const DIMENSIONS: { key: "schedule" | "scope" | "budget"; label: string }[] = [
  { key: "schedule", label: "Schedule" },
  { key: "scope", label: "Scope" },
  { key: "budget", label: "Budget" },
];

const RAG_COLUMNS: (keyof RagCounts)[] = ["green", "yellow", "red", "not_set"];

const RAG_COLUMN_LABEL: Record<keyof RagCounts, string> = {
  green: RAG_META.green.label,
  yellow: RAG_META.yellow.label,
  red: RAG_META.red.label,
  not_set: "Not set",
};

const RAG_DOT_CLASS: Record<keyof RagCounts, string> = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  red: "bg-red-500",
  not_set: "bg-slate-300",
};

export interface RagDistributionCardProps {
  data: RagDistribution | undefined;
  isLoading?: boolean;
  className?: string;
}

export function RagDistributionCard({
  data,
  isLoading = false,
  className,
}: RagDistributionCardProps): JSX.Element {
  const total = data ? data.reported + data.no_report : 0;

  return (
    <Card className={className}>
      <CardHeader>
        <h3 className="text-base font-semibold text-text-primary">
          Project Health (RAG)
        </h3>
        <p className="text-sm text-text-secondary">
          From each active project&apos;s latest submitted weekly report
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3" data-testid="rag-loading">
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-28 w-full rounded-lg" />
          </div>
        ) : !data || total === 0 ? (
          <p className="py-8 text-center text-sm text-text-secondary">
            No active projects to report on.
          </p>
        ) : (
          <div className="space-y-6">
            <ul className="space-y-2" aria-label="Overall status">
              {OVERALL_ROWS.map(({ key, label }) => {
                const count =
                  key === "no_report" ? data.no_report : data.overall[key];
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <li key={key} className="flex items-center gap-3 text-sm">
                    <span className="w-40 shrink-0 text-text-primary">
                      {label}
                    </span>
                    <span
                      className="h-2 flex-1 overflow-hidden rounded-full bg-outlines"
                      aria-hidden="true"
                    >
                      <span
                        className={cn(
                          "block h-full rounded-full",
                          OVERALL_BAR_CLASS[key],
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </span>
                    <span className="w-16 shrink-0 text-right font-semibold text-text-primary">
                      {count}
                      <span className="sr-only"> of {total} projects</span>
                    </span>
                  </li>
                );
              })}
            </ul>

            <Table>
              <caption className="sr-only">
                Schedule, scope and budget RAG counts across {data.reported}{" "}
                reporting projects
              </caption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Dimension</TableHead>
                  {RAG_COLUMNS.map((c) => (
                    <TableHead key={c} scope="col" className="text-center">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className={cn(
                            "h-2 w-2 rounded-full",
                            RAG_DOT_CLASS[c],
                          )}
                          aria-hidden="true"
                        />
                        {RAG_COLUMN_LABEL[c]}
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {DIMENSIONS.map(({ key, label }) => (
                  <TableRow key={key}>
                    <TableHead
                      scope="row"
                      className="font-medium text-text-primary"
                    >
                      {label}
                    </TableHead>
                    {RAG_COLUMNS.map((c) => (
                      <TableCell
                        key={c}
                        className="text-center text-text-primary"
                      >
                        {data[key][c]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
